import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "@/modules/app/useAppContext";
import { getTenant } from "@/helpers/tenant";
import { isFreeStack } from "@/helpers/tenant";
import { getDatastores } from "@/helpers/apis";
import { RobotBGCard } from "@/components/card";
import { Button, Card, CardContent, CardHeader, Typography } from "@altimateai/lego";
import alertIcon from "@/assets/icons/freemium_alert.svg";
import { VSCodeExtensionEnterpriseIcon } from "@/assets/images";
import freemium_api_image from "@/assets/images/freemium_api.svg";
import SettingsCard from "./components/settingsCard";
import SettingsCardWithRegenerate from "./components/settingsCard/SettingsCardWithRegenerate";
import RegenerateSuccess from "./components/regenerateSuccess";
import { Stack } from "@altimateai/lego";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const GET_DATASTORES = "GET-DATASTORES";

const ApiKey = () => {
  useBreadcrumb("API Keys", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });

  const { loggedinUser } = useAppState();
  const [showRegenerateSuccess, setShowRegenerateSuccess] = useState(false);
  const [showAddDatastore, setShowAddDatastore] = useState(false);
  const [showVSCodeImage, setShowVSCodeImage] = useState(false);
  const [authToken, setAuthToken] = useState(loggedinUser?.auth_token || "");

  const isFreeUser = isFreeStack();
  const tenant = getTenant();

  const { data: datastoresData } = useQuery({
    queryKey: [GET_DATASTORES],
    queryFn: getDatastores,
  });

  // Handle successful datastores data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (datastoresData?.data_stores.length === 0) {
      setShowAddDatastore(true);
    }
  }, [datastoresData]);

  const toggleVSCodeImage = () => {
    setShowVSCodeImage(prev => !prev);
  };

  const toggleRegenerateSuccess = () => {
    setShowRegenerateSuccess(prev => !prev);
  };

  return (
    <main aria-labelledby="settings-title">
      {showRegenerateSuccess && <RegenerateSuccess toggleShow={toggleRegenerateSuccess} />}
      <h1 id="settings-title" className="al-sr-only">
        API Settings
      </h1>
      <div role="region" aria-label="API credentials">
        <Stack justify="between" className="al-w-full">
          <SettingsCardWithRegenerate
            cardTitle="Your API Key"
            cardText="Please use the below API key with Altimate CLI and Extensions"
            copyText={authToken}
            setAuthToken={setAuthToken}
            showRegenerateSuccess={() => {
              setShowRegenerateSuccess(true);
            }}
          />
          <SettingsCard
            cardTitle="Your Instance name"
            cardText="Please use the below Instance name with Altimate CLI and API"
            copyText={tenant}
          />
        </Stack>
        {!isFreeUser && showAddDatastore && (
          <div className="al-w-1/2">
            <RobotBGCard>
              <div>
                <Typography variant="subheading">Add datastore for better results.</Typography>
                <Typography variant="body" className="al-mb-4">
                  Only metadata will be used to give better context to AI agent.
                </Typography>
                <Link to="/settings/datastores/add">
                  <Button variant="default" aria-label="Add Connection for better results">
                    Add Connection
                  </Button>
                </Link>
              </div>
            </RobotBGCard>
          </div>
        )}
      </div>

      {isFreeUser ? (
        <aside aria-label="VS Code Extension help">
          <div>
            <Button
              variant="ghost"
              onClick={toggleVSCodeImage}
              aria-expanded={showVSCodeImage}
              aria-label="Toggle VS Code API key location image"
              className="al-bg-white al-rounded-full al-p-2 al-w-1/2 !al-justify-start al-text-left al-mt-4"
            >
              <img src={alertIcon} alt="" aria-hidden="true" />
              <Typography variant="body">Where to add API key in VS Code Extension</Typography>
            </Button>
          </div>

          {showVSCodeImage && (
            <img
              src={freemium_api_image}
              alt="Screenshot showing where to add API key in VS Code Extension"
            />
          )}
        </aside>
      ) : (
        <aside aria-label="VS Code Extension help" className="al-mt-4 al-w-1/2">
          <Card>
            <CardHeader>
              <Typography variant="section-heading">For Enterprise users</Typography>
            </CardHeader>
            <CardContent>
              <Typography variant="body" className="al-mb-4">
                Select &quot;https://api.getaltimate.com&quot; for &quot;Altimate Url&quot; in the
                VS Code Extension settings after adding the API key and instance name.
              </Typography>
              <img src={VSCodeExtensionEnterpriseIcon} alt="VS Code Extension Enterprise" />
            </CardContent>
          </Card>
        </aside>
      )}
    </main>
  );
};

export default ApiKey;
